import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { MoneyLabel } from "../../../../../../components/general/MoneyLabel.tsx";
import type {
    SalesInvoiceDetail,
    SalesInvoiceDetailLine,
    SalesInvoiceVatBreakdownEntry,
} from "../../../components/salesInvoices/types/salesInvoice.ts";
import { formatDateDDMMYYYY } from "../../../components/salesInvoices/utils/salesInvoicesFormat.ts";
import type {SalesInvoiceDisplayStatus, SalesInvoiceLineUnit} from "../../../../../../generated/enums.ts";

const UNIT_LABELS: Record<SalesInvoiceLineUnit, string> = {
    hour: "uur",
    project: "project",
    month: "maand",
    day: "dag",
    piece: "stuks",
    km: "km",
};

function parseCents(value: string | number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.round(parsed) : 0;
}

function formatQuantity(quantity: string | number): string {
    const parsed = Number(quantity);
    if (!Number.isFinite(parsed)) return String(quantity);
    return new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 2 }).format(parsed);
}

function vatRateLabel(vatPercentage: number, vatCategory: string): string {
    if (vatCategory === "E") return "vrijgesteld";
    if (vatCategory === "AE") return "verlegd";
    return `${vatPercentage}%`;
}

function SectionLabel({ children }: { children: string }) {
    return (
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-2">
            {children}
        </p>
    );
}

function LineRow({ line, currency }: { line: SalesInvoiceDetailLine; currency: string }) {
    return (
        <tr className="border-b border-gray-100">
            <td className="py-3 pr-4 text-sm font-semibold text-gray-900">{line.description}</td>
            <td className="py-3 pr-4 text-sm text-gray-700 text-right whitespace-nowrap">
                {formatQuantity(line.quantity)} {UNIT_LABELS[line.unit] ?? line.unit}
            </td>
            <td className="py-3 pr-4 text-sm text-gray-700 text-right whitespace-nowrap">
                <MoneyLabel
                    value={parseCents(line.unit_price_net_cents) / 100}
                    showCurrency={true}
                    currency={currency}
                />
            </td>
            <td className="py-3 pr-4 text-sm text-gray-700 text-right whitespace-nowrap">
                {vatRateLabel(line.vat_percentage, line.vat_category)}
            </td>
            <td className="py-3 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                <MoneyLabel
                    value={parseCents(line.subtotal_cents) / 100}
                    showCurrency={true}
                    currency={currency}
                />
            </td>
        </tr>
    );
}

interface Props {
    invoice: SalesInvoiceDetail;
    displayStatus: SalesInvoiceDisplayStatus;
    logoDataUri: string | null;
}

export function InvoiceDocumentCard({ invoice, displayStatus, logoDataUri }: Props) {
    const isIssued = invoice.invoice_number !== null;

    return (
        <div className="p-8 bg-white rounded-xl shadow-sm border border-gray-100">
            {displayStatus === "draft" && (
                <div className="mb-8 inline-flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2.5">
                    <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">
                        Concept — nog niet verstuurd
                    </span>
                </div>
            )}
            <div className="flex items-start justify-between gap-4">
                <h2 className="text-3xl font-semibold text-gray-900">Factuur</h2>
                {logoDataUri && (
                    <img
                        src={logoDataUri}
                        alt={invoice.seller_business_name}
                        className="max-h-14 max-w-[220px] object-contain"
                    />
                )}
            </div>

            <div className="mt-8 grid gap-8 sm:grid-cols-2">
                <div>
                    <SectionLabel>Klant</SectionLabel>
                    <div className="text-sm text-gray-700 space-y-0.5">
                        <p className="font-medium text-gray-900">{invoice.customer_name}</p>
                        {invoice.customer_address_extra_line && (
                            <p>{invoice.customer_address_extra_line}</p>
                        )}
                        <p>
                            {invoice.customer_address_street} {invoice.customer_address_house_number}
                            {", "}
                            {invoice.customer_address_postal_code} {invoice.customer_address_city}
                        </p>
                        {invoice.customer_vat_number && <p>Btw-nr. {invoice.customer_vat_number}</p>}
                    </div>
                </div>
                <div>
                    <SectionLabel>Leverancier</SectionLabel>
                    <div className="text-sm text-gray-700 space-y-0.5">
                        <p className="font-medium text-gray-900">{invoice.seller_business_name}</p>
                        <p>
                            {invoice.seller_address_street} {invoice.seller_address_house_number}
                        </p>
                        <p>
                            {invoice.seller_address_postal_code}, {invoice.seller_address_city}
                        </p>
                        {invoice.seller_business_website && <p>{invoice.seller_business_website}</p>}
                        <p className="pt-1.5">
                            <span className="font-medium text-gray-900">KVK</span>{" "}
                            {invoice.seller_registration_number}
                        </p>
                        <p>
                            <span className="font-medium text-gray-900">BTW</span>{" "}
                            {invoice.seller_vat_number}
                        </p>
                    </div>
                </div>
                <div>
                    <SectionLabel>Contactpersoon</SectionLabel>
                    <div className="text-sm text-gray-700 space-y-0.5">
                        <p className="font-medium text-gray-900">{invoice.seller_contact_name}</p>
                        <p>{invoice.seller_email}</p>
                    </div>
                </div>
                <div>
                    <SectionLabel>Factuurgegevens</SectionLabel>
                    <div className="text-sm text-gray-700 space-y-0.5">
                        <p>
                            Factuurnummer{" "}
                            {invoice.invoice_number ?? "Concept — nummer volgt bij versturen"}
                        </p>
                        <p>Factuurdatum {formatDateDDMMYYYY(invoice.invoice_date)}</p>
                        <p>Vervaldatum {formatDateDDMMYYYY(invoice.due_date)}</p>
                        {invoice.purchase_order_reference && (
                            <p>PO-nummer {invoice.purchase_order_reference}</p>
                        )}
                    </div>
                </div>
            </div>

            {(invoice.customer_specific_reference || invoice.fixed_footer_text) && (
                <div className="mt-10 space-y-4">
                    {invoice.customer_specific_reference && (
                        <p className="border-l-2 border-gray-200 pl-4 text-sm text-gray-700">
                            {invoice.customer_specific_reference}
                        </p>
                    )}
                    {invoice.fixed_footer_text && (
                        <p className="border-l-2 border-gray-200 pl-4 text-sm text-gray-600">
                            {invoice.fixed_footer_text}
                        </p>
                    )}
                </div>
            )}

            <div className="mt-10 overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="pb-2 pr-4 text-left text-xs font-medium uppercase tracking-wider text-gray-400">
                                Omschrijving
                            </th>
                            <th className="pb-2 pr-4 text-right text-xs font-medium uppercase tracking-wider text-gray-400">
                                Aantal
                            </th>
                            <th className="pb-2 pr-4 text-right text-xs font-medium uppercase tracking-wider text-gray-400">
                                Stukprijs
                            </th>
                            <th className="pb-2 pr-4 text-right text-xs font-medium uppercase tracking-wider text-gray-400">
                                Btw
                            </th>
                            <th className="pb-2 text-right text-xs font-medium uppercase tracking-wider text-gray-400">
                                Totaal
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice.lines.map((line) => (
                            <LineRow key={line.id} line={line} currency={invoice.currency} />
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-6 flex justify-end">
                <div className="w-full max-w-sm space-y-2">
                    <div className="flex items-center justify-between text-sm text-gray-700">
                        <span>Totaal excl. btw</span>
                        <MoneyLabel
                            value={parseCents(invoice.total_excluding_vat_cents) / 100}
                            showCurrency={true}
                            currency={invoice.currency}
                        />
                    </div>
                    {invoice.vat_breakdown.map((entry: SalesInvoiceVatBreakdownEntry) => (
                        <div
                            key={`${entry.vat_category}-${entry.vat_percentage}`}
                            className="flex items-center justify-between text-sm text-gray-700"
                        >
                            <span>Btw ({vatRateLabel(entry.vat_percentage, entry.vat_category)})</span>
                            <MoneyLabel
                                value={parseCents(entry.vat_amount_cents) / 100}
                                showCurrency={true}
                                currency={invoice.currency}
                            />
                        </div>
                    ))}
                    <div className="flex items-center justify-between border-t border-gray-900 pt-3 text-base font-semibold text-gray-900">
                        <span>Totaal te betalen</span>
                        <MoneyLabel
                            value={parseCents(invoice.total_payable_cents) / 100}
                            showCurrency={true}
                            currency={invoice.currency}
                        />
                    </div>
                </div>
            </div>

            {isIssued && (
                <div className="mt-10 flex flex-wrap items-center gap-6 rounded-lg bg-gray-50 p-6">
                    <p className="min-w-[240px] flex-1 text-sm leading-6 text-gray-700">
                        Totaal van{" "}
                        <strong className="text-gray-900">
                            <MoneyLabel
                                value={parseCents(invoice.total_payable_cents) / 100}
                                showCurrency={true}
                                currency={invoice.currency}
                            />
                        </strong>
                        , binnen <strong className="text-gray-900">{invoice.payment_term} dagen</strong>{" "}
                        betalen aan <strong className="text-gray-900">{invoice.bank_account_iban}</strong>{" "}
                        ({invoice.bank_account_owner_name}) met omschrijving{" "}
                        <strong className="text-gray-900">{invoice.invoice_number}</strong>.{" "}
                        <strong className="text-gray-900">Bedankt!</strong>
                    </p>
                </div>
            )}

        </div>
    );
}
